import { Head } from 'vite-react-ssg';

export interface BreadcrumbItem {
  name: string;
  url: string;
}

interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  noindex?: boolean;
  ogImage?: string;
  breadcrumbs?: BreadcrumbItem[];
}

const DEFAULT_OG_IMAGE = 'https://ezcloud.ai/og-social.jpg';

// Bump whenever the OG images are regenerated (npm run og-images) — the query
// string changes every og:image URL, so browsers and social scrapers that
// cached the previous version re-fetch instead of serving a stale card.
// NOTE: never request the next version's URL before its deploy is live (e.g.
// in a deploy-watch poll) — that primes the edge cache with the old bytes
// under the new key. v2 was burned exactly that way.
const OG_VERSION = 5;

// MUST match slugForPath() in scripts/generate-og-images.mjs. Every sitemap
// URL gets a generated PNG in public/og/, so deriving is safe for any
// canonical page; explicit ogImage props still win.
function derivedOgImage(canonicalUrl: string): string {
  try {
    const path = new URL(canonicalUrl).pathname;
    const slug =
      path === '/' ? 'home' : path.replace(/^\//, '').replace(/\/$/, '').replace(/\//g, '--');
    return `https://ezcloud.ai/og/${slug}.png?v=${OG_VERSION}`;
  } catch {
    return DEFAULT_OG_IMAGE;
  }
}

function normalizeCanonical(input?: string): string {
  if (input) return input;
  if (typeof window === 'undefined') return 'https://ezcloud.ai/';
  const path = window.location.pathname;
  const normalizedPath = path === '/' ? '/' : path.replace(/\/$/, '');
  return `https://ezcloud.ai${normalizedPath}`;
}

const SEO = ({
  title,
  description,
  canonical,
  noindex = false,
  ogImage,
  breadcrumbs,
}: SEOProps) => {
  const canonicalUrl = normalizeCanonical(canonical);
  const ogImageUrl = ogImage ?? derivedOgImage(canonicalUrl);

  const breadcrumbSchema = breadcrumbs?.length
    ? {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbs.map((item, idx) => ({
          '@type': 'ListItem',
          position: idx + 1,
          name: item.name,
          item: item.url,
        })),
      }
    : null;

  return (
    <Head>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={noindex ? 'noindex, nofollow' : 'index, follow'} />
      <link rel="canonical" href={canonicalUrl} />

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="EZ Cloud AI" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@ezcloud_ai" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImageUrl} />

      {breadcrumbSchema && (
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      )}
    </Head>
  );
};

export default SEO;
